import axios from 'axios';
import {store} from 'src/redux/store';
import {setJwtToken} from 'src/redux/slice/accountSlice';
import {failureHandler, successHandler} from './interceptors'; // ✅ Import interceptors
import LocalDB from 'src/utils/localDB';
import {authServiceRefresh} from 'src/apis/autoGenerate/services';
import {AuthRefreshRequest} from 'src/apis/autoGenerate/types';
import message from 'src/components/Message';
// import {authServiceRefresh} from 'src/apis';

const apiInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/entrio/api',
  timeout: 30000,
});

// 添加环境变量检查和警告
if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn(
    '⚠️ REACT_APP_API_BASE_URL is not defined. Using default: http://localhost:3000/entrio/api'
  );
}

console.log('🚀 ~ process.env.REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);

// Prevent one kicked session from spawning a toast/redirect per in-flight request
let handlingSessionInvalid = false;

// ✅ Attach token in request interceptor
apiInstance.interceptors.request.use(
  config => {
    const state = store.getState();
    let token = state.account.jwtToken || LocalDB.get(LocalDB.alias.token) || '';

    if (token) {
      // New auth session — allow SESSION_INVALID handling again if kicked later
      handlingSessionInvalid = false;
      config.headers.set('Authorization', `Bearer ${token}`);
    }

    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }

    return config;
  },
  error => Promise.reject(error)
);

// ✅ Use `interceptors.ts` for response handling (NO NEED for duplicate handling here)
apiInstance.interceptors.response.use(
  response => successHandler(response),
  async error => {
    const originalRequest = error.config;

    // Handle SESSION_INVALID error (session kicked out by another device)
    if (error.response?.data?.code === 'SESSION_INVALID') {
      if (handlingSessionInvalid) {
        return Promise.reject(error);
      }
      handlingSessionInvalid = true;

      console.warn('⚠️ Session invalidated by another device login');

      // Clear all authentication data
      LocalDB.remove(LocalDB.alias.token);
      LocalDB.remove(LocalDB.alias.refreshToken);
      LocalDB.remove(LocalDB.alias.sessionId);
      LocalDB.remove(LocalDB.alias.sessionExpiresAt);
      LocalDB.remove(LocalDB.alias.userId);
      LocalDB.remove(LocalDB.alias.role);
      LocalDB.remove(LocalDB.alias.organization);

      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('sessionExpiresAt');
      localStorage.removeItem('userId');

      // Clear Redux state
      store.dispatch(setJwtToken(''));

      const sessionMessage =
        error.response.data.message ||
        'Your account has been logged in from another device. Please log in again.';

      console.log('🔒 ' + sessionMessage);

      // Toast once on login after redirect (full reload clears any in-page toasts).
      // If already on login, show a single warning here.
      if (window.location.pathname.includes('/login')) {
        message.warning(sessionMessage, 5000);
      } else {
        window.location.href = '/login?sessionExpired=true';
      }

      return Promise.reject(error);
    }

    // Never try token refresh for credential login — a 401 is a wrong password (or similar), not an
    // expired access token. Refresh attempts here can fail and replace the real API error message.
    const requestPath = String(originalRequest.url || '');
    const isAuthLoginRequest = requestPath.includes('/auth/login');

    // Only attempt to refresh token on 401 errors and if we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthLoginRequest) {
      originalRequest._retry = true;

      try {
        // Get the refresh token
        const refreshToken = LocalDB.get(LocalDB.alias.refreshToken);

        if (refreshToken) {
          // Use the proper authServiceRefresh method with the correct request object
          const refreshRequest: AuthRefreshRequest = {
            token: refreshToken,
          };

          const response = await authServiceRefresh(refreshRequest);

          // 确保响应数据存在
          if (response?.data?.jwtToken) {
            const newToken = response.data.jwtToken;

            // Store the new token
            LocalDB.set(LocalDB.alias.token, newToken);

            // Update Redux store with the new token
            store.dispatch(setJwtToken(newToken));

            // Update the Authorization header
            apiInstance.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Retry the original request with the new token
            return apiInstance(originalRequest);
          } else {
            throw new Error('Invalid refresh response');
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed', refreshError);

        // Clear authentication data
        LocalDB.remove(LocalDB.alias.token);
        LocalDB.remove(LocalDB.alias.refreshToken);
        LocalDB.remove(LocalDB.alias.sessionId);
        LocalDB.remove(LocalDB.alias.sessionExpiresAt);
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('sessionExpiresAt');

        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return failureHandler(error);
  }
);

export default apiInstance;
